/**
 * Map vote-state + engine-signal + autoSource + isPast onto three
 * orthogonal CSS class channels for scheduler slot cells.
 *
 * H12 visual model — three channels are independent:
 *
 *   - fillClass:   cell background colour, reflects ONLY the user's vote.
 *   - glowClass:   box-shadow halo, reflects the engine signal.
 *   - borderClass: H12 conflict-aware dashed borders:
 *       "conflict-override-urgent" → scheduler-border-override-urgent (red dashed)
 *       "conflict"                 → scheduler-border-conflict (amber dashed)
 *       anything else              → "" (solid default)
 *
 *   Prior to H12, borderClass was driven by autoSource ("engine wrote the
 *   vote → dashed"). H12 replaces that with conflict-awareness: the border
 *   signals *why* the engine acted, not just that it did. The autoSource arg
 *   is kept in the signature for backward compat but is no longer used for
 *   borderClass computation.
 *
 * `isPast` wins over everything else — past slots get a single
 * desaturate+disable class and no glow/border. Callers should still
 * apply their own past-slot border styling separately if they want it.
 *
 * This utility is pure. G2 migrated all callsites and deleted the older
 * slot-tint.js + .scheduler-tint-* vocabulary; this is now the single
 * source of truth for slot visuals.
 *
 * @param {Object} args
 * @param {"PREFERRED"|"FEASIBLE"|null|undefined|string} args.voteState
 *   The user's effective vote for this slot. Anything other than
 *   "PREFERRED" or "FEASIBLE" (including null/undefined/"NONE"/
 *   "NOT_FEASIBLE") is treated as no-vote.
 * @param {"conflict"|"conflict-override-urgent"|"preferred-align"|"feasible-align"|"no-group-match"|null|undefined|string} args.engineKind
 *   The engine signal for this slot. "no-group-match" intentionally
 *   returns "" — the info-tone signal is icon-only, no glow.
 *   "conflict-override-urgent" is a UI-synthesized kind (the caller
 *   computes it from autoVoteEnabled && voteAutoSource === "auto-conflict-override"
 *   plus unacknowledged refIds); the engine itself only emits "conflict".
 * @param {string|null|undefined} args.autoSource
 *   Kept for backward compat; no longer used to derive borderClass as of H12.
 * @param {boolean} args.isPast
 * @returns {{fillClass: string, glowClass: string, borderClass: string}}
 */
export function slotVisualsFor({ voteState, engineKind, autoSource, isPast }) {
  if (isPast) {
    return { fillClass: "scheduler-slot-past", glowClass: "", borderClass: "" };
  }

  const fillClass =
    voteState === "PREFERRED"
      ? "scheduler-vote-fill-preferred"
      : voteState === "FEASIBLE"
        ? "scheduler-vote-fill-feasible"
        : "scheduler-vote-fill-none";

  const glowClass =
    engineKind === "conflict-override-urgent"
      ? "scheduler-glow-override-urgent"
      : engineKind === "conflict"
        ? "scheduler-glow-conflict"
        : engineKind === "preferred-align"
          ? "scheduler-glow-preferred-align"
          : engineKind === "feasible-align"
            ? "scheduler-glow-feasible-align"
            : "";

  // H12: border is driven by conflict type, NOT by autoSource.
  const borderClass =
    engineKind === "conflict-override-urgent"
      ? "scheduler-border-override-urgent"
      : engineKind === "conflict"
        ? "scheduler-border-conflict"
        : "";

  // autoSource is intentionally unused for borderClass as of H12.
  void autoSource;

  return { fillClass, glowClass, borderClass };
}

/**
 * Phase 3 M5 — pick the SlotAutoIndicator chip variant for a slot.
 *
 * Returns `{ mode, isOverride }` where `mode` is "auto-cast" |
 * "auto-submitted" | null, and `isOverride` is true only for Tier 3
 * (submit-override) slots where the engine overrode a manual vote (i.e.
 * `previousManualVote` was captured).
 *
 *   Tier 0 ("off")               → always null
 *   Tier 1 ("cast")              → "auto-cast" if engine pre-filled
 *                                  (voteProposed[slot]) and no vote yet
 *   Tier 2 ("submit")            → "auto-submitted" if engine wrote the vote
 *   Tier 3 ("submit-override")   → "auto-submitted" + isOverride if the
 *                                  engine overrode a manual vote
 *
 * Always returns `null` when the user has already touched this slot
 * (userTouchedSlots) — a manual vote on top of an engine vote shouldn't
 * advertise the engine's authorship anymore.
 *
 * @param {Object} args
 * @param {"off"|"cast"|"submit"|"submit-override"|string|null|undefined} args.autoVoteMode
 * @param {string|null|undefined} args.voteAutoSource voteAutoSource[slotId];
 *   non-null + non-"manual" means the engine drove this slot.
 * @param {boolean} args.voteProposed        voteProposed[slotId] === true
 * @param {boolean} args.hasVote             votes[slotId] is set to a real
 *   level (PREFERRED/FEASIBLE)
 * @param {boolean} args.isUserTouched       slot is in userTouchedSlots
 * @param {*} [args.previousManualVote]      voteAutoReason[slotId].previousManualVote
 *
 * @returns {{mode: "auto-cast"|"auto-submitted"|null, isOverride: boolean}}
 */
export function pillVariantFor({
  autoVoteMode,
  voteAutoSource,
  voteProposed,
  hasVote,
  isUserTouched,
  previousManualVote = null,
}) {
  const NONE = { mode: null, isOverride: false };
  // userTouchedSlots normally suppresses the chip — the user owns the vote.
  // Phase 3a-cascade T5 exception: when the engine has overridden a manual
  // vote at Tier 3 (free-block bumps manual FEASIBLE → PREFERRED), it
  // captures `previousManualVote` and we MUST surface the chip + Restore so
  // the user can see what changed and revert.
  if (isUserTouched && previousManualVote == null) return NONE;

  if (autoVoteMode === "cast") {
    if (voteProposed === true && !hasVote) {
      return { mode: "auto-cast", isOverride: false };
    }
    return NONE;
  }

  if (autoVoteMode === "submit" || autoVoteMode === "submit-override") {
    if (!voteAutoSource || !hasVote) return NONE;
    // OVERRIDE = the engine modified an existing vote on this slot. Both Tier
    // 2 and Tier 3 surface this — the engine writes `previousManualVote` in
    // either auto-conflict-override (Tier 2/3) or future free-block-bumps-
    // manual-vote (Tier 3) flows. Today the conflict-override flavor leaves
    // votes[slot] cleared so it falls out via the !hasVote check above; the
    // chip's OVERRIDE variant currently only fires for the
    // free-block-bumped-prior-vote flavor.
    const isOverride = previousManualVote != null;
    return { mode: "auto-submitted", isOverride };
  }

  return NONE;
}

/**
 * Whether the given voteAutoReason still contains unacknowledged conflict
 * refIds. Used by the UI (SlotIndicator + SchedulerPage's slotVisualsFor
 * callsites + vote-dialog) to synthesize the "conflict-override-urgent"
 * engine kind when:
 *
 *   voteAutoSource[slotId] === "auto-conflict-override"
 *   && reason.kind === "conflict"
 *   && hasUnacknowledgedRefIds(reason)
 *
 * The engine never appends to acknowledgedRefIds; only Restore/Confirm
 * (Chunk G6) do. So a NEW refId from a future calendar-sync re-fires the
 * urgent treatment even after the user has acknowledged the previous set.
 *
 * @param {object|null|undefined} reason  voteAutoReason[slotId]
 * @returns {boolean}
 */
export function hasUnacknowledgedRefIds(reason) {
  if (!reason) return false;
  const eventIds = (reason.events || []).map((e) => e?.eventId).filter(Boolean);
  const busyIds = (reason.busyWindows || [])
    .map((b) => b?.sourceSchedulerId)
    .filter(Boolean);
  const currentIds = [...eventIds, ...busyIds];
  if (currentIds.length === 0) return false;
  const ack = Array.isArray(reason.acknowledgedRefIds) ? reason.acknowledgedRefIds : [];
  return currentIds.some((id) => !ack.includes(id));
}
